import React, { useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core/styles";
import { Button, CircularProgress, Fab, TextField } from "@material-ui/core";
import axios from "axios";
import api from "../common/api";
import EditIcon from "@material-ui/icons/Edit";

const ClientInvoicingConfigs = ({ client_id }) => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [filters, setFilters] = useState([[], [], [], [], [], [], []]);
  const [orderColumn, setOrderColumn] = useState("");
  const [orderOrder, setOrderOrder] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [invoiceDateRange, setInvoiceDateRange] = useState({
    from: "",
    to: ""
  });

  const getMuiTheme = () =>
    createMuiTheme({
      overrides: {
        MuiTableCell: {
          root: {
            padding: "0.5rem",
            fontSize: "0.8rem"
          }
        }
      }
    });

  const fetchData = async (page, filterList, column, order, q_rows) => {
    setIsLoading(true);
    let url = api
      .clientInvoicingConfigs()
      .index(page + 1, "", "", column, order, q_rows);
    if (filterList[1].length !== 0) {
      url += `&business_name=${filterList[1].join(",")}`;
    }
    if (filterList[2].length !== 0) {
      url += `&plate=${filterList[2].join(",")}`;
    }
    if (filterList[3].length !== 0) {
      const [from, to] = filterList[3];
      if (from) url += `&invoice_date_from=${from}`;
      if (to) url += `&invoice_date_to=${to}`;
    }
    if (client_id) {
      url += `&client_id=${client_id}`;
    }
    const token = localStorage.getItem("userToken");
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data.data);
      setCount(res.data.meta.total);
      setRowsPerPage(res.data.meta.per_page);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page, filters, orderColumn, orderOrder, rowsPerPage);
  }, [page, filters, orderColumn, orderOrder, rowsPerPage]);

  const columns = [
    {
      name: "id",
      label: "ID",
      options: {
        filter: false,
        sort: true,
        sortDirection: orderColumn === "id" ? orderOrder : null
      }
    },
    {
      name: "client.business_name",
      label: "Cliente",
      options: {
        filter: true,
        filterType: "textField",
        sort: false,
        customBodyRender: (value, tableMeta) => {
          return (
            <a
              href={`/clients/${tableMeta.rowData[0]}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none"
              style={{ color: "#000" }}
            >
              {value}
            </a>
          );
        }
      }
    },
    {
      name: "items",
      label: "Cantidad items",
      options: {
        filter: false,
        customBodyRender: (value, tableMeta) => {
          if (value.length > 0) {
            // split value.name by comma
            const itemNames = value
              .map(item =>
                item.service_charge && item.service_charge.charge
                  ? item.service_charge.charge.name
                  : ""
              )
              .join(", ");
            return (
              <span>
                {value.length} ({itemNames})
              </span>
            );
          }
          return value ? value.length : 0;
        }
      }
    },
    {
      name: "start_date",
      label: "Fecha Inicio",
      options: {
        filter: false,
        display: false,
        sort: true,
        sortDirection: orderColumn === "start_date" ? orderOrder : null
      }
    },
    {
      name: "end_date",
      label: "Fecha Fin",
      options: {
        filter: false,
        display: false,
        sort: true,
        sortDirection: orderColumn === "end_date" ? orderOrder : null
      }
    },
    {
      name: "billing_frequency",
      label: "Frecuencia",
      options: {
        filter: false,
        sort: true,
        sortDirection: orderColumn === "billing_frequency" ? orderOrder : null
      }
    },
    {
      name: "billing_type",
      label: "Tipo de Factura",
      options: {
        filter: false,
        display: false,
        sort: true,
        sortDirection: orderColumn === "billing_type" ? orderOrder : null
      }
    },
    {
      name: "billing_day",
      label: "Dia de facturacion",
      options: {
        filter: false,
        sort: true,
        sortDirection: orderColumn === "billing_day" ? orderOrder : null
      }
    },
    {
      name: "last_billing_date",
      label: "Fecha ult factura",
      options: {
        filter: false,
        sort: true,
        sortDirection: orderColumn === "last_billing_date" ? orderOrder : null
      }
    },
    {
      name: "next_billing_date",
      label: "Fecha prox factura",
      options: {
        filter: false,
        sort: true,
        sortDirection: orderColumn === "next_billing_date" ? orderOrder : null
      }
    },
    {
      name: "billing_split",
      label: "Cargo Fraccionado",
      options: {
        display: false,
        filterType: "checkbox",
        filterOptions: {
          names: ["Si", "No"]
        },
        customBodyRender: (value, tableMeta) => {
          return value ? "Si" : "No";
        }
      }
    },
    {
      name: "id",
      label: "Acciones",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          return (
            <Fab
              color="secondary"
              aria-label="edit"
              variant="extended"
              style={{ boxShadow: "none" }}
              fontSize="small"
              className="MuiButtonBase-root MuiFab-sizeSmall mr-2"
              onClick={e => {
                window.location.href = `/client-invoicing-config/${value}`;
              }}
            >
              <EditIcon fontSize="small" />
            </Fab>
          );
        }
      }
    }
  ];

  const options = {
    serverSide: true,
    count,
    page,
    rowsPerPage,
    selectableRows: false,
    onChangePage: setPage,
    onChangeRowsPerPage: number => setRowsPerPage(number),
    onColumnSortChange: (column, direction) => {
      setOrderColumn(column);
      setOrderOrder(direction);
    },
    customFilterDialogFooter: filterList => {
      return (
        <div style={{ marginTop: "40px" }}>
          <Button
            variant="contained"
            onClick={() => {
              setPage(0);
              setFilters(filterList);
            }}
          >
            Aplicar
          </Button>
        </div>
      );
    },

    textLabels: {
      body: {
        noMatch: isLoading ? (
          <CircularProgress size={24} />
        ) : (
          "No hay datos disponibles"
        )
      }
    }
  };

  return (
    <MuiThemeProvider theme={getMuiTheme()}>
      <MUIDataTable
        title="Configuraciones de Facturacion"
        data={data}
        columns={columns}
        options={options}
      />
    </MuiThemeProvider>
  );
};

export default ClientInvoicingConfigs;
